import React, { useEffect, useRef, useState } from 'react';
import { RadioButton } from '@repo/common';
import ReactDOM from 'react-dom';
import { JOB_LABEL, cn } from './model/derive';
import usePresence from './usePresence';
import TdButton from './Button';
import TdSelect from './TdSelect';
import useScrollLock from './useScrollLock';

/**
 * 2days 모달 공통 틀 — 제목·부제·본문·하단 버튼. 길면 본문만 스크롤된다.
 * 배경을 누르거나 ESC 로 닫힌다. 진행 중인 저장이 없으니 언제 닫아도 잃는 게 없다.
 * sheetOnMobile 이면 모바일(640px 미만)에서 아래에서 올라오는 시트로 보인다.
 * open 을 넘기면(늘 렌더하고 열림만 바꾸는 방식) 열고 닫을 때 전환이 붙는다 —
 * 모바일 시트는 아래에서 올라오고 아래로 내려가며, 그 밖에는 흐려지며 나타나고 사라진다.
 */
export const Modal = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-[416px]',
  sheetOnMobile = false,
  open,
}) => {
  const animated = open !== undefined;
  const { mounted, shown } = usePresence(animated ? open : true);
  const bodyRef = useRef(null);
  const [scroll, setScroll] = useState({ top: false, bottom: false });

  useEffect(() => {
    const el = bodyRef.current;
    if (!mounted || !el) return undefined;
    const update = () => {
      const top = el.scrollTop > 0;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setScroll((s) => (s.top === top && s.bottom === bottom ? s : { top, bottom }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [mounted]);

  useScrollLock(mounted);
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mounted]);

  if (!mounted) return null;
  const on = !animated || shown;

  return ReactDOM.createPortal(
    <div
      data-keep-panel
      className={cn(
        'fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4',
        sheetOnMobile && 'max-sm:items-end max-sm:p-0',
        animated && 'transition-opacity duration-300 motion-reduce:transition-none',
        on ? 'opacity-100' : 'opacity-0',
      )}
      onMouseDown={onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-grayscale-gray00 shadow-4',
          width,
          sheetOnMobile && 'max-sm:max-w-none max-sm:rounded-b-none',
          animated &&
            'transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none',
          animated &&
            (sheetOnMobile
              ? on
                ? 'max-sm:translate-y-0 sm:scale-100 sm:opacity-100'
                : 'max-sm:translate-y-full sm:scale-[0.97] sm:opacity-0'
              : on
                ? 'scale-100 opacity-100'
                : 'scale-[0.97] opacity-0'),
        )}
      >
        <div
          className={cn(
            'shrink-0 border-b px-6 pt-6 sm:px-7 sm:pt-7',
            children != null ? 'pb-5' : 'pb-1',
            scroll.top ? 'border-green-deep03/[0.08]' : 'border-transparent',
          )}
        >
          <h2 className='text-[20px] font-medium leading-[130%] text-text-primary [overflow-wrap:anywhere]'>{title}</h2>
          {subtitle && (
            <p className='mt-1 text-[14px] leading-[140%] text-text-secondary [overflow-wrap:anywhere]'>{subtitle}</p>
          )}
        </div>
        <div
          ref={bodyRef}
          className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 [scrollbar-color:#d5d2cd_transparent] [scrollbar-width:thin] sm:px-7'
        >
          {children != null && <div className='flex flex-col gap-5 py-1'>{children}</div>}
        </div>
        {footer && (
          <div
            className={cn(
              'flex shrink-0 justify-end gap-2 border-t px-6 pb-6 pt-6 sm:px-7 sm:pb-7',
              scroll.bottom ? 'border-green-deep03/[0.08]' : 'border-transparent',
            )}
          >
            {footer}
          </div>
        )}
        {!footer && <div className='h-5 shrink-0 sm:h-6' />}
      </div>
    </div>,
    document.body,
  );
};

/** 입력 이름표. 필수면 빨간 *, 선택이면 (optional) */
export const Field = ({ label, required, optional, htmlFor, hint, children }) => (
  <div className='flex flex-col gap-2'>
    <label htmlFor={htmlFor} className='text-[13px] font-medium leading-[140%] text-text-primary'>
      {label}
      {required && <span className='text-functional-failed03'>*</span>}
      {optional && <span className='font-normal text-text-secondary'> (optional)</span>}
    </label>
    {children}
    {hint && <p className='text-[12px] leading-[140%] text-text-secondary'>{hint}</p>}
  </div>
);

const control =
  'w-full rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-3 text-[14px] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none';

export const TextInput = (props) => (
  <input {...props} className={cn(control, 'h-10', props.className)} />
);

export const Select = ({ children, className, ...props }) => (
  <TdSelect {...props} className={cn(control, 'h-10 pr-2.5', className)}>
    {children}
  </TdSelect>
);

export const TextArea = (props) => (
  <textarea {...props} className={cn(control, 'min-h-[88px] resize-y py-2.5', props.className)} />
);

/** 하단 버튼 — Cancel(연한 초록) / 주 행동(초록). 입력이 덜 되면 주 행동은 흐리게 */
/** 모달 아래 버튼 — 2days 버튼(BaseButton) 그대로. primary 면 초록, 아니면 연한 초록 */
export const ModalButton = ({ primary, disabled, children, ...props }) => (
  <TdButton variant={primary ? 'primary' : 'soft'} disabled={disabled} {...props}>
    {children}
  </TdButton>
);

/** 팀원 고르기 — 이탈한 팀원과 팀장은 빼고, exclude 에 든 사람도 뺀다 */
export const PersonSelect = ({
  members,
  exclude = [],
  value,
  onChange,
  id,
  className,
  withJob,
}) => (
  <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={className}>
    <option value=''>Select a person</option>
    {members
      .filter((m) => m.status === 'active' && m.role !== 'npc' && !exclude.includes(m.id))
      .map((m) => (
        <option key={m.id} value={m.id}>
          {withJob && JOB_LABEL[m.job] ? `${m.name} · ${JOB_LABEL[m.job]}` : m.name}
        </option>
      ))}
  </Select>
);

/**
 * 라디오 묶음 — 시안의 원형 라디오 (선택: 두꺼운 초록 테두리 + 흰 가운데 / 미선택: 회색 테두리).
 * 브라우저 기본 라디오는 OS 마다 모양이 달라서 직접 그린다. 방향키로 옮겨 다닐 수 있다.
 *
 * @param options [{ value, label }]
 */
/** label 을 비우면 이름표 없이 선택지만 그린다 (바깥에서 질문을 따로 그릴 때) — 그때는 ariaLabel 을 준다 */
export const RadioGroup = ({ label, ariaLabel, required, value, onChange, options }) => {
  const groupRef = useRef(null);
  const radios = () => Array.from(groupRef.current?.querySelectorAll('[role="radio"]') ?? []);
  // 공용 RadioButton은 tabIndex를 받지 않아서 — 선택된 것만 Tab으로 들어오게(roving tabindex) DOM에서 맞춘다
  useEffect(() => {
    radios().forEach((el, i) => {
      el.tabIndex = options[i]?.value === value ? 0 : -1;
    });
  });
  const move = (e) => {
    const step =
      e.key === 'ArrowDown' || e.key === 'ArrowRight'
        ? 1
        : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
          ? -1
          : 0;
    if (!step) return;
    const els = radios();
    const i = els.indexOf(e.target);
    if (i < 0) return;
    e.preventDefault();
    const n = (i + step + options.length) % options.length;
    onChange(options[n].value);
    els[n]?.focus();
  };
  return (
    <div className='flex flex-col gap-3'>
      {label && (
        <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
          {label}
          {required && <span className='text-functional-failed03'>*</span>}
        </span>
      )}
      <div
        ref={groupRef}
        role='radiogroup'
        aria-label={label || ariaLabel}
        onKeyDown={move}
        className='flex flex-col gap-4'
      >
        {options.map((o) => (
          <RadioButton
            key={o.value}
            size='sm'
            checked={o.value === value}
            onChange={() => onChange(o.value)}
            ariaLabel={typeof o.label === 'string' ? o.label : undefined}
          >
            <span
              aria-hidden={typeof o.label === 'string' || undefined}
              onClick={() => onChange(o.value)}
              className='ml-1.5 cursor-pointer text-left text-[14px] leading-[140%] text-text-primary'
            >
              {o.label}
            </span>
          </RadioButton>
        ))}
      </div>
    </div>
  );
};
